<script setup>
import useCategoryTag, {USE_CATEGORY_TAG_KEY} from "./CategoryTagComposition";
import {ref, onMounted, provide, computed} from "vue";
import CampaignContainer from "./CampaignContainer.vue";
import CategoryContainer from "./CategoryContainer.vue";
import CategoryTagList from "./CategoryTagList.vue";
import useMedia from "./PortableMediaComposition";
import draggable from 'vuedraggable';
import CampaignCategoryContainer from "./CampaignCategoryContainer.vue";

//グローバルの siteUrl, uploadDirectory をテンプレート内で使用する
const _siteUrl = siteUrl+'/';

//コンポジションの注入
const composition = useCategoryTag();
provide(USE_CATEGORY_TAG_KEY, composition);
const mediaComposition = useMedia();

//0 以上だとカテゴリ編集モーダルを表示する
const editCategoryIndex = ref(-1);

//0 以上だとタグ削除モーダルを表示する
const deleteCategoryIndex = ref(-1);

//0 以上だとカテゴリ削除モーダルを表示する
const deleteTagIndex = ref(-1);

//追加タグの名称
const appendTagName = ref('');

//編集中のカテゴリ
const editCategory = computed(() => composition.state.categories.length -1 < editCategoryIndex.value ? null : composition.state.categories[editCategoryIndex.value]);

//編集中のカテゴリがキャンペーンに追加されている場合は情報を返す
const editCampaignCategory = computed(() => {
  //編集中カテゴリが存在しなければ undefined
  if (editCategory.value === null) {
    return undefined;
  }

  //検索結果を返す
  return composition.state.categoryCampaigns.find(campaign => Number(campaign.category_id) === Number(editCategory.value.id));
});

//保存ボタンがクリックされた際に更新値として使用される otherCategoryId
//null は変動していない(=更新しない)
//負の値は新規追加したカテゴリに対して設定
//0 はどのカテゴリにも設定しない
//正の値は既存のカテゴリに対して設定
const reservationOtherCategoryId = ref(null);

//エラー文字列の一次元配列
const errors = ref([]);

//成功画面を表示する
const viewToast = () => {
  //成功表示用のトーストを得る
  const toast = document.querySelector('.js-successToast');

  //トーストがあれば表示
  if (toast) {
    toast.style.display = '';
    toast.classList.add('isDsply');
    setTimeout(() => { toast.classList.remove('isDsply') }, 1500);
  }
};

//カテゴリ編集モーダルを表示
const openEditCategory = index => {
  //キャンペーンを同期する
  composition.syncTemporaryCampaignTagIds(index);

  //モーダルを表示する
  editCategoryIndex.value = index;

  //更新時に使用される otherCategoryId を初期化
  setTimeout(() => {
    //現在の設定が自身の id と一致するなら自身の id を仕込む
    reservationOtherCategoryId.value = Number(editCategory.value.id) === Number(composition.state.otherCategoryId)
        ? Number(editCategory.value.id)
        : null;
  }, 100);
}

//カテゴリ編集モーダルを閉じる
const closeEditCategory = () => {
  editCategoryIndex.value = -1;
};

//タグ削除モーダルを表示する
const openDeleteTag = index => {
  deleteTagIndex.value = Number(index);
};

//タグ削除モーダルを閉じる
const closeDeleteTag = () => {
  deleteTagIndex.value = -1;
};

//カテゴリ削除モーダルを開く
const openDeleteCategoryModal = () => {
  deleteCategoryIndex.value = editCategoryIndex.value;
};

//カテゴリ削除モーダルを閉じる
const closeDeleteCategoryModal = () => {
  deleteCategoryIndex.value = -1;
};

//キャンペーンを追加・削除する
const changeTemporaryCampaign = async (payload) => {
  composition.changeTemporaryCampaign(Number(payload.tagId), payload.check);
};

/**
 * カテゴリ名を更新
 * @param payload
 */
const updateCategoryName = event => {
  //不正な DOM を弾く
  if (
      ! (event.target instanceof HTMLInputElement) ||
      event.target.type !== 'text'
  ) {
    return;
  }

  //更新する
  composition.state.categories.splice(editCategoryIndex.value, 1, {...composition.state.categories[editCategoryIndex.value], name: event.target.value});
};

//カテゴリ欄を増やす
const appendEmptyCategory = () => {
  composition.appendEmptyCategory();
};

//カテゴリの更新・新規タグの追加を行う
const setCategory = async () => {
  //エラーを空にする
  errors.value = [];

  //サーバーに更新を依頼する
  const result = await composition.setCategory(editCategoryIndex.value, reservationOtherCategoryId.value);

  //成功したらトーストを表示
  if (result) {
    viewToast();
    return true;
  }

  //失敗したらエラーを表示
  else {
    errors.value = composition.state.errors;
    return false;
  }
};

//タグを追加する
const appendTag = async () => {
  //成功したら入力欄をクリアし、サーバーと同期を試みる
  if ( ! composition.appendTag(appendTagName.value, editCategoryIndex.value)) {
    //同期
    const result = await setCategory();

    //入力欄をクリアする
    if (result) {
      appendTagName.value = '';
    }
  }

  //失敗したらエラーメッセージを表示
  else {
    errors.value = composition.state.errors;
  }
};

//タグを削除する
const deleteTag = async () => {
  //エラーを空にする
  errors.value = [];

  //削除を依頼
  const result = await composition.deleteTag(editCategoryIndex.value, deleteTagIndex.value);

  //成功したら削除対象を初期化してモーダルを閉じる
  if (result) {
    //初期化
    deleteTagIndex.value = -1;

    //トーストの表示
    viewToast();
  }

  //失敗したらエラーを表示
  else {
    errors.value = composition.state.errors;
  }
};

//タグを削除する
const deleteCategory = async () => {
  //エラーを空にする
  errors.value = [];

  //削除を依頼
  const result = await composition.deleteCategory(deleteCategoryIndex.value);

  //成功したらモーダルを閉じる
  if (result) {
    //初期化
    deleteCategoryIndex.value = -1;
    editCategoryIndex.value = -1;

    //トーストの表示
    viewToast();
  }

  //失敗したらエラーを表示
  else {
    errors.value = composition.state.errors;
  }
};

//カテゴリキャンペーンを付け外しする
const toggleCategoryCampaign = async () => {
  //エラーを空にする
  errors.value = [];

  //現在設定されているカテゴリが無ければ終了
  if (editCategoryIndex.value === -1) {
    return;
  }

  //現在設定しているカテゴリがキャンペーン設定されていなければ追加、されていれば削除する
  const result = await editCampaignCategory.value === undefined
      ? composition.setCategoryCampaign(Number(editCategory.value.id))
      : composition.deleteCategoryCampaign(Number(editCategory.value.id));

  //成功したら成功表示
  if (result) {
    viewToast();
  }

  //失敗したらエラーを表示
  else {
    errors.value = composition.state.errors;
  }
};

//カテゴリがメニュー・選択肢に表示されるかをトグル
const toggleCategoryIsView = () => {
  //現在設定されているカテゴリが無ければ終了
  if (editCategoryIndex.value === -1) {
    return;
  }

  //更新する
  composition.state.categories.splice(editCategoryIndex.value, 1, {...composition.state.categories[editCategoryIndex.value], is_view: Number(editCategory.value.is_view) === 0 ? 1: 0});
};

//更新の際に使用されるカテゴリー id の値を変更する
const toggleOtherCategoryId = () => {
  //現在編集中のカテゴリが未選択ならそのカテゴリを更新予約に入れる
  if (Number(reservationOtherCategoryId.value) === 0) {
    reservationOtherCategoryId.value = Number(editCategory.value.id);
  }

  //現在編集中のカテゴリが選択中なら、0 を更新対象とする
  else if (Number(composition.state.otherCategoryId) === Number(editCategory.value.id)) {
    reservationOtherCategoryId.value = 0;
  }

  //そうでなければ null (=更新しない)をセットする]
  else {
    reservationOtherCategoryId.value = null;
  }
};

//カテゴリのソート結果を DB に保持する
const sort = async () => {
  //エラーを空にする
  errors.value = [];

  //並び替えを依頼
  const result = await composition.setCategoryOrders();

  //成功表示
  if (result) {
    viewToast();
  }

  //失敗したらエラーを表示
  else {
    errors.value = composition.state.errors;
  }
};

//読込
onMounted(() => {
  //サーバーからのデータを読み込む
  composition.init();

  //メディアの準備
  mediaComposition.init();

  //メディアの監視
  const thumbnailWatcher = () => {
    //編集モーダルを編集中は何もしない
    if ( ! editCategory.value) {
      setTimeout(thumbnailWatcher, 200);
      return;
    }

    //対象を得る
    const image = document.getElementById('js-columnThumbnail');

    //対象の src を同期させる
    if (image && image instanceof HTMLImageElement) {
      composition.state.categories.splice(editCategoryIndex.value, 1, {...composition.state.categories[editCategoryIndex.value], thumbnail: image.src.split('/').pop()});
    }

    //ループ
    setTimeout(thumbnailWatcher, 200);
  };
  thumbnailWatcher();
});
</script>

<template>
  <CampaignContainer :_site-url="_siteUrl" />
  <draggable
      v-model="composition.state.categories"
      item-key="id"
      @end="sort"
  >
    <template #item="{element, index}">
      <CategoryContainer
          :index="index"
          :campaigns="composition.state.campaigns"
          :category="element"
          :_site-url="_siteUrl"
          @open-edit-category="openEditCategory"
          @open-delete-tag="openDeleteTag"
      />
    </template>
  </draggable>
  <button
      type="button"
      class="pageCtgryAddBtn"
      @click="appendEmptyCategory"
  >新規カテゴリーを追加</button>
  <teleport to="body">
    <div
        :class="{modModalBack: true, isDsply: editCategoryIndex > -1}"
        @click.self="closeEditCategory"
    >
      <div
          :class="{modModalWindow: true, dGray: true, isDsply: editCategoryIndex > -1}"
      >
        <div class="modModalCloseArea">
          <button
              type="button"
              class="modModalClose"
          ><span class="dIcon"><img
              :src="_siteUrl+'images/close.svg'"
              alt="閉じる"
              @click="closeEditCategory"
          ></span></button>
        </div>
        <div class="modModalScroll">
          <div class="pageIconRegiArea">
            <div class="pageIconPreview"><img
                id="js-columnThumbnail"
                :src="_siteUrl+'public_image/'+(editCategory ? editCategory.thumbnail : 'noNameUser.png')"
                alt=""
            ></div>
            <div class="pageIconRegi">
              <input
                  id="js-columnThumbnail-input"
                  type="hidden"
                  value=""
              >
              <button
                  class="pageIconRegiBtn modModalSwitch js-mediaShowButton js-thumbnailButton"
                  type="button"
              >メディアから画像を選択</button>
              <p class="pFont12">サイズが横120px × 縦120px 以上の正方形の画像を使用してください。</p>
            </div>
          </div>
          <dl
              v-if="editCategory"
              class="pageCtgryEditList"
          >
            <dt>カテゴリー名</dt>
            <dd>
              <div class="lyHorizontalBw">
                <p class="lyW300px pTfInsetShadow lyHorizontalBw">
                  <input
                    :value="editCategory.name"
                    class=""
                    type="text"
                    placeholder="カテゴリー名を入力"
                    @input="updateCategoryName"
                >
                  <button
                  type="button"
                  class="modBasicSecSwitchBtn mdRegist"
                  style="width: 100px;border:0;"
                  @click="setCategory"
                >保存</button>
                </p>
              </div>
            </dd>
            <dt>メニュー&選択肢として表示</dt>
            <dd class="pageCmpgnTagList">
              <label
                  class="pRcLabel"
              >
                <input
                    :checked="Number(editCategory.is_view) !== 0"
                    type="checkbox"
                    name="cpTag"
                    @change="toggleCategoryIsView"
                >
                <div
                    class="pageCmpgnTagWrap"
                    style="background-color:#efefef;border:0;background-color:transparent;"
                >
                  <span class="pRcBtn mdCheck"></span>
                </div>
              </label>
            </dd>
            <dt>どのカテゴリーにも属さない投稿を所属させる</dt>
            <dd class="pageCmpgnTagList">
              <label
                  class="pRcLabel"
                  @click.prevent="toggleOtherCategoryId"
              >
                <input
                    :checked="Number(reservationOtherCategoryId) !== 0 && Number(reservationOtherCategoryId) === Number(editCategory.id)"
                    type="checkbox"
                    name="cpTag"
                >
                <div
                    class="pageCmpgnTagWrap"
                    style="background-color:#efefef;border:0;background-color:transparent;"
                >
                  <span class="pRcBtn mdCheck"></span>
                </div>
              </label>
            </dd>
            <dt>タグ追加・削除・キャンペーンタグ追加</dt>
            <dd>
              <div class="pageCtgryEditTagWrap">
                <ul class="pageTagList">
                  <CategoryTagList
                      v-for="(tag, index) in editCategory.tags"
                      :key="tag.id"
                      :campaign-tag-ids="composition.state.temporaryCampaignTagIds"
                      :category-name="editCategory.name"
                      :tag="tag"
                      :enable-delete="Number(tag.is_deletable) !== 0"
                      :_site-url="_siteUrl"
                      :index="index"
                      @change-check="changeTemporaryCampaign"
                      @delete-tag="openDeleteTag"
                  />
                </ul>
                <label for="tagName" class="pageCtgryEditTagRegi">
                  <input
                      v-model="appendTagName"
                      type="text"
                      class="pageCtgryEditTagInput"
                      placeholder="#タグを作成"
                  >
                  <button
                      type="button"
                      class="pageCtgryEditTagSubmit"
                      @click="appendTag"
                  >追加</button>
                </label>
              </div>
            </dd>
          </dl>
          <button
              v-if="editCategory && Number(editCategory.is_deletable) !== 0"
              type="button"
              class="modBulkSettingBtn mdDelete lyBlockRight lyMt16"
              @click="openDeleteCategoryModal"
          >このカテゴリーを削除</button>
          <p style="color: #d00">{{errors.join(' ')}}</p>
        </div>
      </div>
    </div>
  </teleport>
  <teleport to="body">
    <div :class="{modConfirmModal: true, modDialogBox: true, isDsply: deleteTagIndex > -1}">
      <div>
        <div class="lyHorizontalBw lyVerticalBwCnt lyMl16 lyMr8 lyMt8">
          <p class="pFont16 pFontBold">削除</p>
          <button
            type="button"
            class="modConfirmClose js-confirmHide"
            @click="closeDeleteTag"
          >
        <span class="modConfirmCloseIcon">
          <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.3499 9.94741L19.1376 2.15974L17.7234 0.745529L9.93572 8.5332L2.16031 0.757787L0.746094 2.172L8.52151 9.94741L0.752826 17.7161L2.16704 19.1303L9.93572 11.3616L17.7167 19.1426L19.1309 17.7283L11.3499 9.94741Z" fill="#2D2D2D" />
          </svg>
        </span>
          </button>
        </div>
        <p class="lyMl16 lyMr16 lyMt16">削除してもよろしいですか？</p>
      </div>
      <div class="modConfirmModalButtonsWrapper">
        <button
          type="button"
          class="modConfirmModalButton"
          @click="closeDeleteTag"
        >とじる</button>
        <button
          type="button"
          class="modConfirmModalButton modConfirmModalButtonSubmit"
          @click="deleteTag"
        >削除する</button>
      </div>
    </div>
  </teleport>
  <teleport to="body">
    <div :class="{modConfirmModal: true, modDialogBox: true, isDsply: deleteCategoryIndex > -1}">
      <div>
        <div class="lyHorizontalBw lyVerticalBwCnt lyMl16 lyMr8 lyMt8">
          <p class="pFont16 pFontBold">削除</p>
          <button
            type="button"
            class="modConfirmClose js-confirmHide"
            @click="closeDeleteCategoryModal"
          >
        <span class="modConfirmCloseIcon">
          <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.3499 9.94741L19.1376 2.15974L17.7234 0.745529L9.93572 8.5332L2.16031 0.757787L0.746094 2.172L8.52151 9.94741L0.752826 17.7161L2.16704 19.1303L9.93572 11.3616L17.7167 19.1426L19.1309 17.7283L11.3499 9.94741Z" fill="#2D2D2D" />
          </svg>
        </span>
          </button>
        </div>
        <p class="lyMl16 lyMr16 lyMt16">カテゴリーを削除してよろしいでしょうか？</p>
      </div>
      <div class="modConfirmModalButtonsWrapper">
        <button
          type="button"
          class="modConfirmModalButton"
          @click="closeDeleteCategoryModal"
        >キャンセル</button>
        <button
          type="button"
          class="modConfirmModalButton modConfirmModalButtonSubmit"
          @click="deleteCategory"
        >削除する</button>
      </div>
    </div>
  </teleport>
</template>
